import PostAvatar from "@/components/ui/PostAvatar";
import Span from "@/components/ui/Span";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Iconify from "@/components/ui/Iconify";
import HeartAnimation from "@/components/custom-framer-animations/heart-animation";
import { timeAgo, timeAgoInKorean } from "@/utils/helpers";
import {
  deletePostComment,
  postCommentsDislikes,
  postCommentsLikes,
} from "@/api/comment";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import ContentCommentContent from "../content-comment-content";
import TooltipUserNameheading from "../content-sections/content-details-section/content-tooltip-username-heading";
import { useLocale, useTranslations } from "@/lib/static-intl";
import { useTheme } from "next-themes";

const ContentSubCommentListTile = ({
  comment,
  parentCommentId,
  setAllPostComments,
  selectedPostInModal,
  setCurrentCommentInReply,
  handleReportComment,
  triggerFullScreenImageSlider,
  currentUserFollowing,
  setCurrentUserFollowing,
}) => {
  const locale = useLocale();
  const apiT = useTranslations("api");
  const explorePostDetailsT = useTranslations("explore.explore_post_details");
  const { theme } = useTheme();
  const [cmtMenuAnchorEl, setCmtMenuAnchorEl] = useState(null);
  const commentMenuOpen = Boolean(cmtMenuAnchorEl);

  const { user, openLoginModal } = useAuthContext();
  const [subCommentLike, setSubCommentLike] = useState(comment?.commentLike);
  const [isSubCommentLiked, setIsSubCommentLiked] = useState(
    !!subCommentLike?.find((_cl) => _cl?.userId === user?.userId)
  );

  const handleCmtMenuClick = (event) => {
    if (!user) {
      openLoginModal();
      return;
    }
    setCmtMenuAnchorEl(event.currentTarget);
  };

  const handleCmtMenuClose = () => {
    setCmtMenuAnchorEl(null);
  };

  const likeSubComment = async (userId, commentId) => {
    if (!user) {
      openLoginModal();
      return;
    }
    try {
      if (!user || !comment) return;
      await postCommentsLikes({
        userId,
        commentId,
      });
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const dislikeSubComment = async (userId, commentId) => {
    if (!user) {
      openLoginModal();
      return;
    }
    try {
      if (!user || !comment) return;
      await postCommentsDislikes({
        userId,
        commentId,
      });
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const handleLikeSubComment = () => {
    if (!user) {
      openLoginModal();
      return;
    }

    if (!subCommentLike?.some((like) => like.userId === user?.userId)) {
      setSubCommentLike([
        ...subCommentLike,
        { commentId: comment?.id, userId: user?.userId },
      ]);
      setIsSubCommentLiked(true);
      likeSubComment(user?.userId, comment?.id);
    } else {
      setSubCommentLike(
        subCommentLike?.filter((like) => like.userId !== user?.userId)
      );
      setIsSubCommentLiked(false);
      dislikeSubComment(user?.userId, comment?.id);
    }
  };

  const handleDeleteComment = async (cmtId) => {
    if (!user) {
      openLoginModal();
      return;
    }
    if (!cmtId) return;

    handleCmtMenuClose();
    setAllPostComments((prev) =>
      prev.map((cmt) => {
        if (cmt?.id === parentCommentId) {
          return {
            ...cmt,
            subComments: cmt?.subComments?.filter(
              (subCmt) => subCmt?.id !== cmtId
            ),
          };
        }
        return cmt;
      })
    );
    try {
      await deletePostComment(cmtId);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const getSubCommentLikeText = (subCommentLike) => {
    if (subCommentLike?.length === 0) {
      return explorePostDetailsT("thumbs_up");
    } else if (subCommentLike?.length > 10) {
      return "10+";
    } else {
      return subCommentLike?.length;
    }
  };

  useEffect(() => {
    setSubCommentLike(comment?.commentLike);
    setIsSubCommentLiked(
      !!comment?.commentLike?.find((_cl) => _cl?.userId === user?.userId)
    );
  }, [comment]);

  return (
    <div className="w-full flex gap-2">
      <div className="h-fit w-fit cursor-pointer">
        <TooltipUserNameheading
          username={comment?.user?.username}
          currentUserFollowing={currentUserFollowing}
          setCurrentUserFollowing={setCurrentUserFollowing}
        >
          <PostAvatar
            className={"!min-w-6 !h-6"}
            src={comment?.user?.profilePicture}
          />
        </TooltipUserNameheading>
      </div>
      <div className="w-full flex flex-col">
        <div className="w-full flex justify-between">
          <TooltipUserNameheading
            username={comment?.user?.username}
            currentUserFollowing={currentUserFollowing}
            setCurrentUserFollowing={setCurrentUserFollowing}
          >
            <Span className="cursor-pointer">{comment?.user?.name}</Span>
          </TooltipUserNameheading>

          <IconButton
            id="comment-reply-menu"
            size="small"
            onClick={handleCmtMenuClick}
          >
            <MoreHorizIcon
              sx={{ fontSize: 14, color: theme === "dark" ? "white" : "black" }}
            />
          </IconButton>
          <Menu
            id="comment-reply-menu"
            anchorEl={cmtMenuAnchorEl}
            open={commentMenuOpen}
            onClose={handleCmtMenuClose}
            MenuListProps={{
              "aria-labelledby": "comment-reply-menu",
              className: 'bg-white dark:bg-[#282828] text-black dark:text-white',
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            style={{
              zIndex: 9999,
            }}
            sx={{
              '& .MuiPaper-root': {
                zIndex: 9999,
              }
            }}
          >
            {[
              comment?.user?.id === user?.userId && (
                <MenuItem
                  sx={{
                    backgroundColor: theme === "dark" ? "#282828" : "#fff",
                    color: theme === "dark" ? "white" : "black",
                    "&:hover": {
                      backgroundColor:
                        theme === "dark" ? "#282828" : "inherit",
                    },
                  }}
                  className="hover:bg-bg-black/5 dark:hover:bg-dark_bg_grey"
                  key="delete"
                  onClick={() => handleDeleteComment(comment?.id)}
                >
                  {explorePostDetailsT("delete")}
                </MenuItem>
              ),
              selectedPostInModal?.user?.id === user?.userId && comment?.user?.id !== user?.userId && (
                <MenuItem
                  sx={{
                    backgroundColor: theme === "dark" ? "#282828" : "#fff",
                    color: theme === "dark" ? "white" : "black",
                    "&:hover": {
                      backgroundColor:
                        theme === "dark" ? "#282828" : "inherit",
                    },
                  }}
                  className="hover:bg-bg-black/5 dark:hover:bg-dark_bg_grey"
                  key="report"
                  onClick={() => {
                    handleCmtMenuClose();
                    handleReportComment(comment);
                  }}
                >
                  {explorePostDetailsT("report")}
                </MenuItem>
              ),
            ]}
          </Menu>
        </div>
        {comment?.commentMedia?.length > 0 && (
          <img
            className="w-32 h-32 rounded object-cover mb-2 cursor-zoom-in"
            alt="comment-reply-media"
            src={comment?.commentMedia[0]?.filePath}
            onClick={() =>
              triggerFullScreenImageSlider(comment?.commentMedia || [])
            }
          />
        )}
        {comment?.content && comment?.content?.length > 0 && (
          <ContentCommentContent
            className="tracking-tight text-sm"
            content={comment?.content}
            taggedUsers={comment?.commentTaggedUsers}
          />
        )}
        <Span className={"!text-xs mt-1 tracking-tighter"}>
          {locale === "en"
            ? timeAgo(comment?.createdAt)
            : timeAgoInKorean(comment?.createdAt)}
        </Span>
        <div className="w-full flex items-center gap-4 mt-1.5">
          <button
            type="button"
            className="group flex items-center gap-1 cursor-pointer"
            onClick={handleLikeSubComment}
          >
            <HeartAnimation
              hasClicked={isSubCommentLiked}
              onClick={() => { }}
              size={16}
            />
            <Span>{getSubCommentLikeText(subCommentLike)}</Span>
          </button>

          <button
            type="button"
            className="group flex items-center gap-1 cursor-pointer"
            onClick={() => setCurrentCommentInReply(comment)}
          >
            <Iconify icon={"mage:message-round"} className={"!w-4 !h-4"} />
            <Span>{explorePostDetailsT("reply")}</Span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContentSubCommentListTile;
